import {useEffect, useRef, useState} from 'react';
import {WaterDTO} from '../../dtos/WaterDTO';
import {AxiosError} from 'axios';
import {NetworkCalls} from '../../NetworkCalls';
import {Alert, Button, Card, Col} from 'react-bootstrap';
import {Link, useNavigate} from 'react-router-dom';
import AddEditGuestCardDialog from './AddEditGuestCardDialog';
import {GuestCardDTO} from '../../dtos/GuestCardDTO';
import LoadingPageIndicator from '../../components/LoadingPageIndicator';
import {ImageChanges} from './image-gallery/imageChanges';
import {useSelectedOrganisationContext} from "../../custom-hooks/useOrganisation";

export default function AddGuestCard() {
  const [loading, isLoading] = useState<boolean>(false);
  const [waters, setWaters] = useState<WaterDTO[]>([]);
  const { selectedOrganisation } = useSelectedOrganisationContext();
  const navigate = useNavigate();
  const [card] = useState<GuestCardDTO>({
    active: true,
    description: '',
    duration: 1,
    organisationId: '',
    price: 10,
    sendWaterRegulation: true,
    termsAndConditions: '',
    waterMappings: [],
    id: ''
  } as GuestCardDTO);
  const [error, setError] = useState<AxiosError | undefined>(undefined);
  const [stripeConnectActive, setStripeConnectActive] = useState<boolean>(false);
  const [stripeCheckLoading, setStripeCheckLoading] = useState<boolean>(true);

  const submitRef = useRef();

  useEffect(() => {
    if (!selectedOrganisation) {
      return;
    }

    isLoading(true);
    setStripeCheckLoading(true);

    card.organisationId = selectedOrganisation.id;

    Promise.all([
      NetworkCalls.listWater(),
      NetworkCalls.getStripeConnectAccountStatus()
    ])
      .then(([waterResult, stripeStatus]) => {
        const sortedWaters = waterResult.sort((a, b) => a.name.localeCompare(b.name));
        setWaters(sortedWaters);
        setStripeConnectActive(stripeStatus.hasAccount && stripeStatus.detailsSubmitted);
      })
      .catch(err => {
        setError(err);
        console.error(err);
      })
      .finally(() => {
        isLoading(false);
        setStripeCheckLoading(false);
      });

  }, [card, selectedOrganisation]);

  const afterSubmit = async (card: GuestCardDTO, images?: ImageChanges) => {
    setError(undefined);
    try {
      const newCard = await NetworkCalls.addGuestCard(card);
      if (images) {
        for (const image of images.images) {
          if (!image.fromServer && image.file) {
            await NetworkCalls.addGuestCardImage(newCard, image.file);
          }
        }
      }
      navigate('..', {relative: 'path'});
    } catch (error) {
      setError(error as AxiosError);
    }
  };

  const renderError = () => {
    if (!error)
      return <></>;
    return <>
      <Alert variant="danger">{error.message}</Alert>
    </>;
  };

  const renderStripeWarning = () => {
    if (stripeCheckLoading || stripeConnectActive) {
      return <></>;
    }
    return (
      <Alert variant="warning" className="mb-3">
        <Alert.Heading>Stripe Connect nicht eingerichtet</Alert.Heading>
        <p>
          Um Gastkarten anzulegen und Zahlungen zu empfangen, müssen Sie zuerst Ihr Stripe Connect Konto einrichten.
        </p>
        <hr />
        <div className="d-flex justify-content-end">
          <Link to="/dashboard/administration/guest-cards/onboarding" className="btn btn-warning">
            Jetzt einrichten
          </Link>
        </div>
      </Alert>
    );
  };

  const render = () => {
    if (loading) {
      return <LoadingPageIndicator />;
    }

    if (!stripeConnectActive && !stripeCheckLoading) {
      return (
        <>
          {renderError()}
          {renderStripeWarning()}
        </>
      );
    }

    return <>
      {renderError()}
      <AddEditGuestCardDialog card={card} afterSubmitAction={afterSubmit} waters={waters} submitRef={submitRef} />
    </>;
  };

  const submitForm = () => {
    // @ts-ignore
    submitRef.current.click();
  };

  return <>
    <Card className="m-5 justify-content-center border-0" as={Col}>
      <Card.Header><h1>Gastkarte hinzufügen</h1></Card.Header>
      <Card.Body className="border-bottom">
        {render()}
      </Card.Body>
      <Card.Footer className="space-x">
        <Button onClick={submitForm} disabled={!stripeConnectActive}>Speichern</Button>
        <Link to={'/dashboard/administration/guest-cards'} className="btn btn-secondary">Abbrechen</Link>
      </Card.Footer>
    </Card>
  </>;

}
